import { type FC, type ReactNode } from 'react'
import classNames from 'classnames'
import { Col, Form, Row } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import { type AccountOpeningHoldersPanel } from '../../../models/AccountOpeningDescriptor'
import { holdersCopy } from '../descriptor/stepCopy'
import {
  asksRelationshipAt,
  defaultNameSource,
  holderSections,
  holderSlots,
  relationshipOptionsFor,
  roleKindIn,
  roleLabelIn,
  roleOptions,
  rolesWithHeadroom,
  sectionIndexOf,
  type HolderSection,
} from '../domain/accountTypeCatalog'
import { HolderPersonPicker } from './HolderPersonPicker'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { clientStatusOf, custodianIdOf, holderClientStatus } from '../domain/contactFallback'
import { WARNING_BORDER } from '../constants'
import { MessagePanel } from '../../../components/MessagePanel/MessagePanel'
import { type ClientStatus, type ContactOption, type WizardAccount, type WizardHolder } from '../domain/types'

interface AccountHolderFieldsProps {
  accountTypes: AccountType[]
  selectors: AccountOpeningTypeSelector[]
  panel: AccountOpeningHoldersPanel
  account: WizardAccount
  onChange: (account: WizardAccount) => void
  disabled?: boolean
  contactOptions: ContactOption[]
  defaultHolderContactId?: string | null
  accountId: string
  connector: string
}

export const AccountHolderFields: FC<AccountHolderFieldsProps> = ({
  accountTypes,
  selectors,
  panel,
  account,
  onChange,
  disabled = false,
  contactOptions,
  defaultHolderContactId,
  accountId,
  connector,
}) => {
  const copy = holdersCopy(panel, selectors.length, selectors.map((selector) => selector.label.toLowerCase()).join(' and '))
  const availableRoles = roleOptions(accountTypes, account.typeId)
  const sections = holderSections(accountTypes, account.typeId)
  const slots = holderSlots(accountTypes, account.typeId)
  const requiredSlotCount = slots.length
  const relationshipSelectOptions: SelectOption[] = relationshipOptionsFor(accountTypes, account.typeId).map((option) => ({
    id: option.value,
    label: option.label,
  }))
  const rolesForNewHolder = rolesWithHeadroom(accountTypes, account.typeId, account.holders)
  const launchSlotIndex = slots.findIndex((slot) => slot.kind === 'individual')

  const updateHolder = (holderIndex: number, patch: Partial<WizardHolder>) => {
    onChange({ ...account, holders: account.holders.map((holder, index) => (index === holderIndex ? { ...holder, ...patch } : holder)) })
  }

  const handlePersonChange = (holderIndex: number, contact: ContactOption | null) => {
    if (!contact) {
      updateHolder(holderIndex, { nameSource: null, name: '', contactId: null, externalId: undefined })
      return
    }
    const existingClient = holderClientStatus(account.holders[holderIndex]) === 'existing'
    updateHolder(holderIndex, {
      nameSource: 'contact',
      name: contact.name,
      contactId: contact.id,
      externalId: existingClient ? custodianIdOf(contact, connector) : undefined,
    })
  }

  const handleTypeByHand = (holderIndex: number) => updateHolder(holderIndex, { nameSource: 'manual', name: '', contactId: null, externalId: undefined })

  const handleClientStatusChange = (holderIndex: number, clientStatus: ClientStatus) => {
    const holder = account.holders[holderIndex]
    const picked = holder.nameSource === 'contact' ? contactOptions.find((option) => option.id === holder.contactId) : undefined
    if (picked && clientStatusOf(picked, connector) === clientStatus) {
      updateHolder(holderIndex, { clientStatus, externalId: clientStatus === 'existing' ? custodianIdOf(picked, connector) : undefined })
      return
    }
    updateHolder(holderIndex, {
      clientStatus,
      nameSource: clientStatus === 'existing' ? null : defaultNameSource(contactOptions),
      name: '',
      contactId: null,
      externalId: undefined,
    })
  }

  const addHolder = (roleCode: string) => {
    onChange({
      ...account,
      holders: [
        ...account.holders,
        {
          roleCode,
          nameSource: roleKindIn(accountTypes, roleCode) === 'organization' ? 'manual' : defaultNameSource(contactOptions),
          name: '',
          contactId: null,
          clientStatus: 'new',
        },
      ],
    })
  }

  const removeHolder = (holderIndex: number) => {
    onChange({ ...account, holders: account.holders.filter((_, index) => index !== holderIndex) })
  }

  const inSection = (section: HolderSection) => (option: { value: string }) =>
    section.heading === null ? sectionIndexOf(sections, option.value) === 0 : section.roles.includes(option.value)

  const sectionCopy = (section: HolderSection, roleCode: string) => {
    if (section.heading === null) {
      return copy
    }
    const noun = roleLabelIn(accountTypes, roleCode).toLowerCase()
    return {
      ...copy,
      heading: section.heading,
      personNamePlaceholder: `Add ${noun} name`,
      addLabel: `Add ${noun}`,
      removeLabel: `Remove ${noun}`,
    }
  }

  const renderHolder = (holder: WizardHolder, holderIndex: number, section: HolderSection): ReactNode => {
    const rowCopy = sectionCopy(section, holder.roleCode)
    const hasRoleChoice = availableRoles.filter(inSection(section)).length > 1
    const slot = slots[holderIndex]
    const isEntity = (slot?.kind ?? roleKindIn(accountTypes, holder.roleCode)) === 'organization'
    const isLockedToLaunchContact =
      holderIndex === launchSlotIndex && !!defaultHolderContactId && holder.contactId === defaultHolderContactId && holder.name.trim().length > 0
    const isRemovable = holderIndex >= requiredSlotCount && !disabled && !isLockedToLaunchContact
    const rolesForThisHolder = rolesWithHeadroom(accountTypes, account.typeId, account.holders, holderIndex).filter(inSection(section))
    const asksRelationship = asksRelationshipAt(accountTypes, account.typeId, holderIndex)
    const roleOptions: SelectOption[] = rolesForThisHolder.map((role) => ({ id: role.value, label: role.label }))
    const fieldId = (field: string): string => `${account.accountRefId}-holder-${holderIndex}-${field}`
    const clientStatusOptions: SelectOption[] = [
      { id: 'new', label: rowCopy.clientStatusNew },
      { id: 'existing', label: rowCopy.clientStatusExisting },
    ]
    return (
      <Row key={`${account.accountRefId}-holder-${holderIndex}`} className='g-3'>
        {isEntity ? null : (
          <Col md={2} className='d-flex flex-column zocks-gap-0.5'>
            <Form.Label htmlFor={fieldId('status')} className='paragraph-small mb-0'>
              {rowCopy.clientStatusLabel}
            </Form.Label>
            <SelectDropdown
              toggleId={fieldId('status')}
              ariaLabel={rowCopy.clientStatusLabel}
              options={clientStatusOptions}
              initialSelectedOption={clientStatusOptions.find((option) => option.id === holderClientStatus(holder)) ?? null}
              disabled={disabled || isLockedToLaunchContact}
              onOptionSelected={(option) => handleClientStatusChange(holderIndex, option.id as ClientStatus)}
            />
          </Col>
        )}
        <Col md={hasRoleChoice && asksRelationship ? 4 : hasRoleChoice || asksRelationship ? 6 : true} className='d-flex flex-column zocks-gap-0.5'>
          <Form.Label htmlFor={fieldId('name')} className='paragraph-small mb-0'>
            {rowCopy.nameLabel}
          </Form.Label>
          {isEntity ? (
            <Form.Control
              id={fieldId('name')}
              type='text'
              placeholder={rowCopy.entityNamePlaceholder}
              value={holder.name}
              disabled={disabled}
              className={classNames({ [WARNING_BORDER]: !holder.name.trim() })}
              onChange={(event) => updateHolder(holderIndex, { name: event.target.value })}
            />
          ) : (
            <>
              <HolderPersonPicker
                accountId={accountId}
                connector={connector}
                holder={holder}
                toggleId={fieldId('name')}
                disabled={disabled || isLockedToLaunchContact}
                suggested={contactOptions}
                onPick={(contact) => handlePersonChange(holderIndex, contact)}
                onTypeByHand={() => handleTypeByHand(holderIndex)}
              />
              {holder.nameSource === 'manual' ? (
                <Form.Control
                  type='text'
                  placeholder={rowCopy.personNamePlaceholder}
                  value={holder.name}
                  disabled={disabled}
                  className={classNames({ [WARNING_BORDER]: !holder.name.trim() })}
                  onChange={(event) => updateHolder(holderIndex, { name: event.target.value })}
                />
              ) : null}
            </>
          )}
        </Col>
        {hasRoleChoice ? (
          <Col md className='d-flex flex-column zocks-gap-0.5'>
            <Form.Label htmlFor={fieldId('role')} className='paragraph-small mb-0'>
              {rowCopy.roleLabel}
            </Form.Label>
            <SelectDropdown
              toggleId={fieldId('role')}
              key={roleOptions.map((option) => option.id).join('|')}
              ariaLabel={rowCopy.roleLabel}
              options={roleOptions}
              initialSelectedOption={roleOptions.find((option) => option.id === holder.roleCode) ?? null}
              disabled={disabled}
              onOptionSelected={(option) => updateHolder(holderIndex, { roleCode: option.id })}
            />
          </Col>
        ) : null}
        {asksRelationship ? (
          <Col md className='d-flex flex-column zocks-gap-0.5'>
            <Form.Label htmlFor={fieldId('relationship')} className='paragraph-small mb-0'>
              {rowCopy.relationshipLabel}
            </Form.Label>
            <SelectDropdown
              toggleId={fieldId('relationship')}
              ariaLabel={rowCopy.relationshipLabel}
              options={relationshipSelectOptions}
              initialSelectedOption={relationshipSelectOptions.find((option) => option.id === holder.relationship) ?? null}
              placeholder={rowCopy.relationshipUnset}
              disabled={disabled}
              showDeselectOption
              deselectOptionLabel={rowCopy.relationshipUnset}
              onOptionSelected={(option) => updateHolder(holderIndex, { relationship: option.id })}
              onDeselect={() => updateHolder(holderIndex, { relationship: undefined })}
            />
          </Col>
        ) : null}
        {isRemovable ? (
          <Col xs='auto' className='d-flex flex-column zocks-gap-0.5'>
            <Form.Label className='paragraph-small mb-0'>&nbsp;</Form.Label>
            <Button variant='secondary' aria-label={rowCopy.removeLabel} onClick={() => removeHolder(holderIndex)}>
              <i className='bi bi-trash' />
            </Button>
          </Col>
        ) : null}
      </Row>
    )
  }

  const renderSection = (section: HolderSection, sectionIndex: number) => {
    const members = account.holders.flatMap((holder, holderIndex) =>
      sectionIndexOf(sections, holder.roleCode) === sectionIndex ? [{ holder, holderIndex }] : [],
    )
    const addable = rolesForNewHolder.filter(inSection(section))
    if (section.heading === null && members.length === 0 && addable.length === 0) {
      return null
    }
    return (
      <div key={section.heading ?? ''} className='d-flex flex-column zocks-gap-1'>
        {section.heading === null ? null : <p className='paragraph-medium medium mb-0'>{section.heading}</p>}
        {section.heading !== null && members.length === 0 ? <p className='paragraph-small zocks-text-gray-600 mb-0'>None added.</p> : null}
        {members.map(({ holder, holderIndex }) => renderHolder(holder, holderIndex, section))}
        {!disabled && addable.length > 0 ? (
          <Button variant='secondary' className='align-self-start d-flex align-items-center zocks-gap-0.5' onClick={() => addHolder(addable[0].value)}>
            <i className='bi bi-plus-circle' />
            {sectionCopy(section, addable[0].value).addLabel}
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div className='d-flex flex-column zocks-gap-1'>
      <p className='paragraph-medium medium mb-0'>{copy.heading}</p>
      {account.typeId && !disabled ? <MessagePanel variant='info' message={<span className='paragraph-small'>{copy.existingClientHint}</span>} /> : null}
      {account.typeId ? sections.map(renderSection) : <p className='paragraph-small zocks-text-gray-600 mb-0'>{copy.awaitingType}</p>}
    </div>
  )
}
